import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import type { MRT_ColumnDef } from 'mantine-react-table';
import { useBookings, type Booking } from '../../hooks/useBookings';
import { useBusinessAccessInvitations, type BusinessAccess } from '../../hooks/useBusinessAccessInvitations';
import { useBookingLinkStatuses } from '../../hooks/useBookingLinkStatuses';
import { useBusinessPaymentsById } from '../../hooks/useBusinessPayments';
import { useFdCan } from '../../hooks/useFdCan';
import { MantineDataTable } from '../../components/MantineDataTable';
import { bookingHref, bookingsByPaymentReference } from '../../components/paymentBookingLink';
import { cancelBusinessAccessPass } from '../../api/passCancel';
import { toDayParam } from '../../api/businessAccessCombined';
import { passFilterStatusId, passTone, tileState, tileText, PASS_STATUS, type PassFilter, type Tone } from './activity';
import { paymentOfPass, formatCurrency, formatCardNumber, lastFour, gatewayLabel, paymentChipOf, netPaidAmount, type PaymentChip } from '../../utils/paymentUtils';
import { formatMoneyFixed } from '../../utils/money';
import { getApiErrorMessage } from '../../utils/apiErrors';
import type { Payment } from '../../types/payment.types';
import { Avatar, DateRangeMenu, Pills, Segmented, StatusChip, StatusMenu } from './ActivityControls';
import { BusinessActivityPhone } from './BusinessActivityPhone';
import { activePhoneTime, phoneTimeRange, type PhoneTime } from './activityPhone';
import type { PhoneRow } from '../../components/phone/PhoneListParts';
import { useIsPhone } from '../../hooks/useIsPhone';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useT } from '../../i18n/admin';
import { ActivityPassDrawer, ActivityPaymentDrawer, type PassDrawerData, type PaymentDrawerContext } from './ActivityDrawers';
import {
  BOOKING_STATUS_FILTERS, TX_CHIP_LABEL, TX_CHIP_TONE, TX_STATUS_FILTERS, bookingStatusView, filterBookings, filterTransactions,
  formatDay, formatVisit, hasRange, initials, normalizeRange, txPillWindow, visitPillWindow,
  type ActivityView, type BookingStatusKey, type DayRange, type TxPill, type TxStatusFilter, type VisitPill,
} from './activityViews';

/**
 * SAM-1186 — Activity of a business, from scratch (canvas v214): four tiles, a Bookings | Passes | Transactions
 * switch, time pills + Date range + Status, a MantineDataTable per view, and drawers in the style of Merchants.
 * New Tailwind component (SAM-175 freeze).
 */

export const ACTIVITY_PASS_PAGE_SIZE = 20;

const VIEWS: Array<{ k: ActivityView; label: string }> = [
  { k: 'bookings', label: 'Bookings' },
  { k: 'passes', label: 'Passes' },
  { k: 'transactions', label: 'Transactions' },
];
const VISIT_PILLS: Array<{ k: VisitPill; label: string }> = [
  { k: 'today', label: 'Today' }, { k: 'upcoming', label: 'Upcoming' }, { k: 'past', label: 'Past' },
];
const TX_PILLS: Array<{ k: TxPill; label: string }> = [
  { k: 'today', label: 'Today' }, { k: '7d', label: '7 days' }, { k: 'month', label: 'This month' },
];
type PassStatusKey = PassFilter | 'cancelled';
const PASS_STATUS_FILTERS: Array<{ k: PassStatusKey; label: string }> = [
  { k: 'any', label: 'All' }, { k: 'pending', label: 'Pending' }, { k: 'approved', label: 'Confirmed' },
  { k: 'in', label: 'Checked in' }, { k: 'out', label: 'Checked out' }, { k: 'cancelled', label: 'Cancelled' },
];

const passStatusId = (f: PassStatusKey): number | undefined => (f === 'cancelled' ? PASS_STATUS.cancelled : passFilterStatusId(f));
const PASS_LABEL: Record<number, string> = {
  1: 'Pending', 2: 'Confirmed', 3: 'Verified', 4: 'Checked in', 5: 'Checked out', 6: 'Cancelled', 7: 'Expired', 8: 'Processing',
};
const passStatusView = (p: BusinessAccess): { label: string; tone: Tone } => {
  const id = Number(p.business_access_status_id ?? 0);
  return { label: PASS_LABEL[id] || p.status_name || `Status ${id}`, tone: passTone(id) };
};
const guestName = (p: BusinessAccess) => [p.main_guest?.name, p.main_guest?.last_name].filter(Boolean).join(' ') || '—';
const passTypeOf = (p: BusinessAccess) => p.type_name || 'Pass';
const passBusinessName = (p: BusinessAccess) => p.business?.name || p.business_name || '';

/** The pass's own charge: the latest one `/combined` reports on the row, else the one the payments list holds for it. */
const passPaymentChip = (p: BusinessAccess, payments: readonly Payment[]): PaymentChip | null => {
  const own = p.payment_status ? ({ payment_status: p.payment_status } as Pick<Payment, 'payment_status'>) : paymentOfPass(payments, String(p.id));
  return own ? paymentChipOf(own as Payment) : null;
};

const countGuests = (b: Booking): number | string => {
  const lines = (Array.isArray(b.booking_options) ? b.booking_options : []) as Array<{ total_guest?: number | string; line_type?: string }>;
  const n = lines.filter((l) => l && l.line_type !== 'add_on' && l.line_type !== 'unit').reduce((s, l) => s + (Number(l.total_guest) || 0), 0);
  return n || '—';
};

const bookingAmount = (b: Booking): string => {
  if (b.amount === null || b.amount === undefined || b.amount === '') return '—';
  const n = Number(b.amount);
  return n > 0 ? formatCurrency(n, b.currency) : 'Free';
};

/** A business pass is valid for whole days: «All day» for today's, the day written out otherwise. */
const passWhen = (p: BusinessAccess, allDay = 'All day'): string => {
  if (!p.start_date) return '—';
  const start = new Date(p.start_date);
  const oneDay = !p.end_date || toDayParam(new Date(p.end_date)) === toDayParam(start);
  return toDayParam(start) === toDayParam(new Date()) && oneDay ? allDay : formatDay(toDayParam(start));
};

const guestCell = (name: string) => (
  <span className="flex items-center gap-2.5"><Avatar initials={initials(name)} /><span className="font-semibold">{name}</span></span>
);

export const BusinessActivity: React.FC<{ businessId: string }> = ({ businessId }) => {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const { t } = useT();
  const perms = useUserPermissions();
  const canCreatePass = perms.isInvitationManager || perms.isResidentialInvitationManager || perms.isAllInvitationManager || perms.isPropertyManager;
  const [customOpen, setCustomOpen] = useState(false);
  const focusPassId = params.get('pass');

  // H5 (SAM-1281): the Transactions view and the payments read follow the role matrix (utils/frontDeskMatrix.ts), as the menu does.
  const canSeeTransactions = useFdCan('transactions', 'view') && !perms.isLoading && !perms.isXhinolaAdmin;
  // SAM-1320 (B9): the combined, /bookings and payments routes answer 403 for `xhinola_admin`, so the file asks none of
  // them and shows no Passes, Transactions or the tiles built from them (four toasts on opening it).
  // SAM-1323 (B9): nor while the groups are still being read: the first render asked them all (and react-query
  // retried each one), which is why the toasts kept coming.
  const readsPassData = !perms.isLoading && !perms.isXhinolaAdmin;
  const [view, setView] = useState<ActivityView>(focusPassId ? 'passes' : 'bookings');
  const [visitPill, setVisitPill] = useState<VisitPill>('today');
  const [txPill, setTxPill] = useState<TxPill | null>('month');
  const [range, setRange] = useState<DayRange>({});
  const [bookingStatus, setBookingStatus] = useState<'all' | BookingStatusKey>('all');
  const [passStatus, setPassStatus] = useState<PassStatusKey>('any');
  const [txStatus, setTxStatus] = useState<TxStatusFilter>('all');
  const [query, setQuery] = useState('');
  const [serverQuery, setServerQuery] = useState('');
  const [passPage, setPassPage] = useState(1);
  const [selectedPassId, setSelectedPassId] = useState<string | null>(focusPassId);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setServerQuery(query.trim()), 350);
    return () => window.clearTimeout(t);
  }, [query]);
  useEffect(() => { setPassPage(1); }, [visitPill, range, passStatus, serverQuery, businessId]);
  useEffect(() => { setQuery(''); }, [view]);
  useEffect(() => { if (focusPassId) { setSelectedPassId(focusPassId); setView('passes'); } }, [focusPassId]);

  // A Date range replaces the pill: picking one clears the pill; picking a pill clears the range.
  const pickRange = (r: DayRange) => { setRange(r); setTxPill(hasRange(r) ? null : 'month'); };
  const pickVisitPill = (p: VisitPill) => { setVisitPill(p); setRange({}); };
  const pickTxPill = (p: TxPill) => { setTxPill(p); setRange({}); };

  /* ----- data ----- */
  const { data: crudBookings, isLoading: loadingBookings, isError: bookingsFailed, refetch: refetchBookings } = useBookings(businessId);
  // The open-payment-link state only comes from GET /bookings; if that list fails the rows stay, without «Awaiting payment».
  const { data: linkStatuses } = useBookingLinkStatuses(businessId, readsPassData);
  const bookings = useMemo(
    () => (crudBookings ?? []).map((b) => (linkStatuses?.[String(b.id)] ? { ...b, payment_link_status: linkStatuses[String(b.id)] } : b)),
    [crudBookings, linkStatuses],
  );
  const scope = { business_id: businessId, entity: 'business_access' as const };
  const passWindow = hasRange(range) ? range : visitPillWindow(visitPill);
  const { data: accessData, isLoading: loadingPasses, isError: passesFailed, refetch: refetchPasses } = useBusinessAccessInvitations({
    ...scope,
    ...(passWindow.from ? { date_from: passWindow.from } : {}),
    ...(passWindow.to ? { date_to: passWindow.to } : {}),
    business_access_status_id: passStatusId(passStatus),
    q: serverQuery || undefined,
    page: passPage,
    limit: ACTIVITY_PASS_PAGE_SIZE,
  }, { enabled: view === 'passes' && readsPassData });
  const today = toDayParam(new Date());
  const { data: passesTodayData, isError: passesTodayFailed } = useBusinessAccessInvitations({ ...scope, date_from: today, date_to: today, limit: 1 }, { enabled: readsPassData });
  // The deep-linked pass (?pass=<id>) or the one opened from a payment: loaded by id wherever it falls.
  const { data: selectedData } = useBusinessAccessInvitations({ ...scope, id: selectedPassId || undefined, limit: 1 }, { enabled: !!selectedPassId && readsPassData });
  const { data: paymentsData, isLoading: loadingPayments, isError: paymentsFailed, refetch: refetchPayments } = useBusinessPaymentsById(businessId, canSeeTransactions);

  const payments = useMemo(() => paymentsData?.payments ?? [], [paymentsData]);
  const passes = useMemo(
    () => (accessData?.data || []).filter((it: BusinessAccess) => it.entity === 'business_access' && String(it.business_id) === String(businessId)),
    [accessData, businessId],
  );
  const selectedPass = selectedPassId ? (selectedData?.data || []).find((p) => String(p.id) === selectedPassId) ?? null : null;
  const bookingByRef = useMemo(() => bookingsByPaymentReference(bookings), [bookings]);
  const bookingById = useMemo(() => new Map(bookings.map((b) => [String(b.id), b])), [bookings]);

  /* ----- tiles ----- */
  const bookingsToday = bookings.filter((b) => b.reservation_start && toDayParam(new Date(b.reservation_start)) === today
    && bookingStatusView(b).key !== 'cancelled').length;
  const awaiting = bookings.filter((b) => bookingStatusView(b).key === 'awaiting' && b.reservation_start && toDayParam(new Date(b.reservation_start)) >= today).length;
  const monthFrom = txPillWindow('month').from ?? '';
  // Net of refunds: a partial refund takes only the refunded part off, a void or full refund takes the whole charge.
  const paidThisMonth = payments
    .filter((p) => (p.payment_date || p.createdAt || '').slice(0, 10) >= monthFrom)
    .reduce((s, p) => s + netPaidAmount(p), 0);
  const bookingsState = bookingsFailed ? 'error' : loadingBookings ? 'loading' : 'ok';
  const allTiles: Array<{ label: string; value: string; go: ActivityView }> = [
    { label: 'Passes today', value: tileText(tileState(passesTodayData, passesTodayFailed), String(passesTodayData?.total_elements ?? 0)), go: 'passes' },
    { label: 'Bookings today', value: tileText(bookingsState, String(bookingsToday)), go: 'bookings' },
    { label: 'Paid this month', value: tileText(tileState(paymentsData, paymentsFailed), formatMoneyFixed(paidThisMonth, 'DOP')), go: 'transactions' },
    { label: 'Awaiting payment', value: tileText(bookingsState, String(awaiting)), go: 'bookings' },
  ];
  const tiles = allTiles.filter((t) => (canSeeTransactions || t.go !== 'transactions') && (readsPassData || (t.go !== 'passes' && t.label !== 'Awaiting payment')));
  const views = VIEWS.filter((v) => (canSeeTransactions || v.k !== 'transactions') && (readsPassData || v.k !== 'passes'));

  /* ----- who paid / what for ----- */
  // Until the payments list carries payer and item names, a payment is named by the booking it paid for
  // (same gateway reference) or by the pass it paid for (loaded on demand, see paymentContext).
  const bookingOfPayment = (p: Payment): Booking | undefined => {
    const ref = p.payment_reference?.trim();
    const hit = ref ? bookingByRef.get(ref) : undefined;
    return hit ? bookingById.get(String(hit.id)) : undefined;
  };
  const paymentPayer = (p: Payment) => p.payer_name || bookingOfPayment(p)?.user_name || '—';
  const paymentFor = (p: Payment) => {
    const named = p.item_name?.en || p.item_name?.es;
    if (named) return named;
    const b = bookingOfPayment(p);
    if (b) return b.service_name || 'Booking';
    return p.business_access_id ? 'Pass' : '—';
  };

  const paymentPassId = selectedPayment?.business_access_id || null;
  const { data: paymentPassData } = useBusinessAccessInvitations({ ...scope, id: paymentPassId || undefined, limit: 1 }, { enabled: !!paymentPassId && readsPassData });
  const paymentContext = (p: Payment): PaymentDrawerContext => {
    const b = bookingOfPayment(p);
    if (b) {
      const st = bookingStatusView(b);
      return {
        title: b.service_name || 'Booking', guest: b.user_name, service: b.service_name,
        linked: { kind: 'booking', id: String(b.id), name: b.user_name || 'Booking', when: formatVisit(b.reservation_start), status: { label: st.label, tone: st.tone } },
      };
    }
    const pass = (paymentPassData?.data || []).find((x) => String(x.id) === String(p.business_access_id));
    if (pass) {
      return {
        title: passTypeOf(pass), guest: guestName(pass), service: passTypeOf(pass),
        linked: { kind: 'pass', id: String(pass.id), name: guestName(pass), when: formatVisit(pass.start_date), status: passStatusView(pass) },
      };
    }
    return { title: p.business_access_id ? 'Pass' : 'Payment' };
  };

  const passDrawer = useMemo<PassDrawerData | null>(() => {
    const pass = selectedPass ?? passes.find((p) => String(p.id) === selectedPassId) ?? null;
    if (!pass) return null;
    const guests = 1 + (Array.isArray(pass.business_guest) ? pass.business_guest.length : 0);
    const statusId = Number(pass.business_access_status_id ?? 0);
    return {
      id: String(pass.id),
      guest: guestName(pass),
      subtitle: [`${passTypeOf(pass)} pass`, passBusinessName(pass)].filter(Boolean).join(' · '),
      type: passTypeOf(pass),
      destination: passBusinessName(pass) || '—',
      valid: pass.start_date ? formatDay(toDayParam(new Date(pass.start_date))) : '—',
      guests: `${guests} ${guests === 1 ? 'guest' : 'guests'}`,
      created: pass.create_date ? formatVisit(pass.create_date) : '—',
      status: passStatusView(pass),
      payment: paymentOfPass(payments, String(pass.id)),
      cancellable: statusId !== PASS_STATUS.cancelled && statusId !== PASS_STATUS.expired && statusId !== PASS_STATUS.out,
    };
  }, [selectedPass, passes, selectedPassId, payments]);

  const closePass = () => {
    setSelectedPassId(null);
    setCancelError(null);
    if (focusPassId) { const p = new URLSearchParams(params); p.delete('pass'); setParams(p, { replace: true }); }
  };
  const cancelPass = (id: string) => {
    setCancelling(true);
    setCancelError(null);
    cancelBusinessAccessPass(id)
      .then(() => closePass())
      .catch((e: unknown) => setCancelError(getApiErrorMessage(e as never, 'Could not cancel the pass')))
      .finally(() => {
        setCancelling(false);
        queryClient.invalidateQueries({ queryKey: ['business-access-invitations'] });
        queryClient.invalidateQueries({ queryKey: ['business-payments'] });
      });
  };

  /* ----- rows ----- */
  const bookingRows = useMemo(
    () => filterBookings(bookings, { pill: visitPill, range, status: bookingStatus, query }),
    [bookings, visitPill, range, bookingStatus, query],
  );
  const txRows = useMemo(() => {
    const window = hasRange(range) ? range : txPillWindow(txPill ?? 'month');
    return filterTransactions(payments, {
      range: window, status: txStatus, query,
      searchText: (p) => `${paymentPayer(p)} ${paymentFor(p)} ${lastFour(p.cardLast4)} ${p.payment_reference ?? ''} ${p.transaction_reference ?? ''}`,
    });
  }, [payments, range, txPill, txStatus, query, bookingByRef, bookingById]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ----- columns ----- */
  const bookingCols = useMemo<MRT_ColumnDef<Booking>[]>(() => [
    { id: 'when', header: 'When', size: 170, Cell: ({ row }) => <span className="tabular-nums whitespace-nowrap">{formatVisit(row.original.reservation_start)}</span> },
    { id: 'guest', header: 'Guest', Cell: ({ row }) => guestCell(row.original.user_name || row.original.user_email || '—') },
    { id: 'service', header: 'Service', Cell: ({ row }) => <span>{row.original.service_name || 'Booking'}</span> },
    { id: 'guests', header: 'Guests', size: 80, Cell: ({ row }) => <span>{countGuests(row.original)}</span> },
    { id: 'amount', header: 'Amount', size: 120, Cell: ({ row }) => <span className="tabular-nums">{bookingAmount(row.original)}</span> },
    { id: 'status', header: 'Status', size: 150, Cell: ({ row }) => { const s = bookingStatusView(row.original); return <StatusChip tone={s.tone} label={s.label} note={s.note} />; } },
  ], []);
  const passCols = useMemo<MRT_ColumnDef<BusinessAccess>[]>(() => [
    { id: 'when', header: 'When', size: 150, Cell: ({ row }) => <span className="tabular-nums whitespace-nowrap">{passWhen(row.original)}</span> },
    { id: 'guest', header: 'Guest', Cell: ({ row }) => guestCell(guestName(row.original)) },
    { id: 'type', header: 'Type', size: 130, Cell: ({ row }) => <span>{passTypeOf(row.original)}</span> },
    { id: 'destination', header: 'Destination', Cell: ({ row }) => <span>{passBusinessName(row.original) || '—'}</span> },
    { id: 'status', header: 'Pass status', size: 140, Cell: ({ row }) => { const s = passStatusView(row.original); return <StatusChip tone={s.tone} label={s.label} />; } },
    {
      id: 'payment', header: 'Payment', size: 130,
      Cell: ({ row }) => {
        const c = passPaymentChip(row.original, payments);
        return c ? <StatusChip tone={TX_CHIP_TONE[c]} label={TX_CHIP_LABEL[c]} /> : <span>—</span>;
      },
    },
    { id: 'created', header: 'Created by', size: 140, Cell: ({ row }) => <span>{row.original.created_by_name || row.original.channel || '—'}</span> },
  ], [payments]);
  const txCols = useMemo<MRT_ColumnDef<Payment>[]>(() => [
    { id: 'time', header: 'Time', size: 150, Cell: ({ row }) => <span className="tabular-nums whitespace-nowrap">{formatVisit(row.original.payment_date || row.original.createdAt)}</span> },
    { id: 'payer', header: 'Payer', Cell: ({ row }) => <span className="font-semibold">{paymentPayer(row.original)}</span> },
    { id: 'for', header: 'For', Cell: ({ row }) => <span>{paymentFor(row.original)}</span> },
    { id: 'method', header: 'Method', size: 140, Cell: ({ row }) => <span>{formatCardNumber(row.original.cardLast4)}</span> },
    { id: 'gateway', header: 'Gateway', size: 120, Cell: ({ row }) => <span>{gatewayLabel(row.original.payment_gateway)}</span> },
    { id: 'amount', header: 'Amount', size: 110, Cell: ({ row }) => <span className="tabular-nums">{formatCurrency(row.original.payment_amount, row.original.currency)}</span> },
    { id: 'status', header: 'Status', size: 130, Cell: ({ row }) => { const c = paymentChipOf(row.original); return c ? <StatusChip tone={TX_CHIP_TONE[c]} label={TX_CHIP_LABEL[c]} /> : <span>—</span>; } },
  ], [bookingByRef, bookingById]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ----- phone (SAM-944): the same data and filters, drawn as cards ----- */
  const phoneTime = customOpen ? 'custom' : activePhoneTime(view, range, visitPill, txPill);
  const pickPhoneTime = (k: PhoneTime | 'custom') => {
    if (k === 'custom') { setCustomOpen(true); return; }
    setCustomOpen(false);
    pickRange(phoneTimeRange(k));
  };
  const phoneStatus = view === 'bookings' ? (bookingStatus === 'all' ? null : bookingStatus)
    : view === 'passes' ? (passStatus === 'any' ? null : passStatus)
      : (txStatus === 'all' ? null : txStatus);
  const pickPhoneStatus = (k: string | null) => {
    if (view === 'bookings') setBookingStatus((k ?? 'all') as 'all' | BookingStatusKey);
    else if (view === 'passes') setPassStatus((k ?? 'any') as PassStatusKey);
    else setTxStatus((k ?? 'all') as TxStatusFilter);
  };
  const phoneRows = useMemo<PhoneRow[]>(() => {
    const asRow = (r: PhoneRow) => r;
    if (view === 'bookings') {
      return bookingRows.map((b) => {
        const st = bookingStatusView(b);
        const n = countGuests(b);
        const service = b.service_name || 'Booking';
        return asRow({
          key: String(b.id),
          when: formatVisit(b.reservation_start),
          today: !!b.reservation_start && toDayParam(new Date(b.reservation_start)) === today,
          status: { label: st.label, tone: st.tone },
          name: b.user_name || b.user_email || '—',
          sub: n === '—' ? service : `${service} · ${t(n === 1 ? 'activityPhone.guestsOne' : 'activityPhone.guestsMany', { n })}`,
          extra: bookingAmount(b),
          open: () => { window.location.assign(bookingHref(String(b.id), businessId)); },
        });
      });
    }
    if (view === 'passes') {
      return passes.map((p) => {
        const st = passStatusView(p);
        const when = passWhen(p, t('phonePasses.allDay'));
        const by = p.created_by_name || p.channel;
        return asRow({
          key: String(p.id),
          when,
          today: when === t('phonePasses.allDay'),
          status: st,
          name: guestName(p),
          sub: [passTypeOf(p), passBusinessName(p)].filter(Boolean).join(' · '),
          extra: by ? t('activityPhone.createdBy', { name: by }) : undefined,
          open: () => setSelectedPassId(String(p.id)),
        });
      });
    }
    return txRows.map((p) => {
      const chip = paymentChipOf(p);
      const at = p.payment_date || p.createdAt;
      return asRow({
        key: `${p.payment_reference ?? ''}:${p.transaction_reference ?? ''}:${at ?? ''}`,
        when: formatVisit(at),
        today: !!at && toDayParam(new Date(at)) === today,
        status: chip ? { label: TX_CHIP_LABEL[chip], tone: TX_CHIP_TONE[chip] } : null,
        name: paymentPayer(p),
        sub: paymentFor(p),
        extra: [formatCardNumber(p.cardLast4), gatewayLabel(p.payment_gateway), formatCurrency(p.payment_amount, p.currency)].filter(Boolean).join(' · '),
        open: () => setSelectedPayment(p),
      });
    });
  }, [view, bookingRows, passes, txRows, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchBox = (placeholder: string) => () => (
    <label className="flex items-center gap-2 h-10 w-72 max-w-full px-3 rounded-lg bg-[var(--line-subtle)] text-[var(--ink-500)]">
      <Search size={16} aria-hidden />
      <input aria-label={placeholder} placeholder={placeholder} value={query} onChange={(e) => setQuery(e.target.value)}
        className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-[var(--ink-900)]" />
    </label>
  );

  const failed = (view === 'bookings' && bookingsFailed) || (view === 'passes' && passesFailed) || (view === 'transactions' && paymentsFailed);
  const retry = () => { if (view === 'bookings') refetchBookings(); else if (view === 'passes') refetchPasses(); else refetchPayments(); };

  return (
    <div className="flex flex-col gap-4">
      {isPhone ? (
        <BusinessActivityPhone
          view={view} onView={setView}
          query={query} onQuery={setQuery}
          time={phoneTime} onTime={pickPhoneTime}
          customOpen={customOpen} range={range} onRange={(r) => pickRange(normalizeRange(r))}
          status={phoneStatus} onStatus={pickPhoneStatus}
          rows={phoneRows}
          loading={view === 'bookings' ? loadingBookings : view === 'passes' ? loadingPasses : loadingPayments}
          failed={failed} onRetry={retry}
          serverPage={view === 'passes' ? { page: passPage, limit: ACTIVITY_PASS_PAGE_SIZE, total: accessData?.total_elements ?? 0, onPage: setPassPage } : undefined}
          onNew={canCreatePass ? () => navigate('/invitations/new') : undefined}
          views={views.map((v) => v.k)}
        />
      ) : (
        <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <button key={t.label} type="button" onClick={() => setView(t.go)}
            className="text-left rounded-xl border border-solid border-[var(--line)] bg-white px-4 py-3.5 flex flex-col gap-1 cursor-pointer">
            <span className="text-[13px] text-[var(--ink-500)]">{t.label}</span>
            <span className="text-2xl font-bold tabular-nums text-[var(--ink-900)]">{t.value}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented label="View" value={view} options={views} onChange={(k) => setView(k)} />
        <div className="flex flex-wrap items-center gap-3">
          {view === 'transactions'
            ? <Pills label="Time" value={txPill} options={TX_PILLS} onChange={pickTxPill} />
            : <Pills label="Time" value={hasRange(range) ? null : visitPill} options={VISIT_PILLS} onChange={pickVisitPill} />}
          <DateRangeMenu value={range} onChange={pickRange} />
          {view === 'bookings' && <StatusMenu value={bookingStatus} options={BOOKING_STATUS_FILTERS} onChange={(k) => setBookingStatus(k)} />}
          {view === 'passes' && <StatusMenu value={passStatus} options={PASS_STATUS_FILTERS} onChange={(k) => setPassStatus(k)} />}
          {view === 'transactions' && <StatusMenu value={txStatus} options={TX_STATUS_FILTERS} onChange={(k) => setTxStatus(k)} />}
        </div>
      </div>

      {failed && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-solid border-[var(--line)] bg-white px-4 py-3 text-[13px] text-[var(--ink-700)]">
          Couldn’t load all of the activity.
          <button type="button" className="h-8 px-3 rounded-lg border border-solid border-[var(--line)] bg-white text-[13px] font-semibold cursor-pointer" onClick={retry}>
            Try again
          </button>
        </div>
      )}

      {view === 'bookings' && (
        <MantineDataTable<Booking>
          columns={bookingCols} data={bookingRows} loading={loadingBookings} enableGlobalFilter={false} enableSorting={false}
          renderTopToolbarCustomActions={searchBox('Search guest or service')}
          onRowClick={(b) => { window.location.assign(bookingHref(String(b.id), businessId)); }}
          emptyStateMessage="No bookings for this filter." containerHeight="auto"
        />
      )}
      {view === 'passes' && (
        <MantineDataTable<BusinessAccess>
          columns={passCols} data={passes} loading={loadingPasses} enableGlobalFilter={false} enableSorting={false}
          manualPagination pageCount={accessData?.total_pages ?? 0} totalRecords={accessData?.total_elements ?? 0}
          initialPageSize={ACTIVITY_PASS_PAGE_SIZE} pageSizeOptions={[ACTIVITY_PASS_PAGE_SIZE]}
          state={{ pagination: { pageIndex: passPage - 1, pageSize: ACTIVITY_PASS_PAGE_SIZE } }}
          onPaginationChange={({ pageIndex }) => setPassPage(pageIndex + 1)}
          renderTopToolbarCustomActions={searchBox('Search guest or type')}
          onRowClick={(p) => setSelectedPassId(String(p.id))}
          emptyStateMessage="No passes for this filter." containerHeight="auto"
        />
      )}
      {view === 'transactions' && canSeeTransactions && (
        <MantineDataTable<Payment>
          columns={txCols} data={txRows} loading={loadingPayments} enableGlobalFilter={false} enableSorting={false}
          renderTopToolbarCustomActions={searchBox('Search payer, card or reference')}
          onRowClick={(p) => setSelectedPayment(p)}
          emptyStateMessage="No transactions for this filter." containerHeight="auto"
        />
      )}

        </>
      )}

      <ActivityPassDrawer
        pass={passDrawer} onClose={closePass} cancelling={cancelling} cancelError={cancelError} onCancel={cancelPass}
        onOpenPayment={(p) => setSelectedPayment(p)}
      />
      <ActivityPaymentDrawer
        payment={selectedPayment} businessId={businessId}
        context={selectedPayment ? paymentContext(selectedPayment) : undefined}
        onClose={() => setSelectedPayment(null)}
        onOpenPass={(id) => { setSelectedPayment(null); setSelectedPassId(id); setView('passes'); }}
      />
    </div>
  );
};
